import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  InputAdornment,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
  Tabs,
  Tab,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Avatar,
  Stack,
  Tooltip,
  Collapse,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { useTranslation } from "react-i18next";
import ErrorPage from "../../../Components/GeneralComponents/errorPage/ErrorPage";
import useFetchObjects from "../../../api/useFetchObjects";
import { useAuth } from "../../../context";
import PersonIcon from "@mui/icons-material/Person";
import MedicalServicesIcon from "@mui/icons-material/MedicalServices";
import EventIcon from "@mui/icons-material/Event";
import TodayIcon from "@mui/icons-material/Today";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import NotesIcon from "@mui/icons-material/Notes";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import PersonPinIcon from "@mui/icons-material/PersonPin";
import GroupsIcon from "@mui/icons-material/Groups";
import formatDateTime from "../../../Components/GeneralComponents/DateFormatter/FormatDateTime";

const tableCellStyle = {
  border: "1px solid #ddd",
  textAlign: "center",
  padding: "4px",
  fontSize: "0.8rem",
};

const getStatusColor = (statusValue, statusDisplay) => {
  if (!statusValue && !statusDisplay) return "default";
  const val = (statusValue || "").toString().toLowerCase();
  const disp = (statusDisplay || "").toString().toLowerCase();
  if (val === "3" || disp.includes("follow")) return "success";
  if (val === "1" || disp.includes("pending")) return "warning";
  if (val === "2" || disp.includes("cancel")) return "error";
  return "info";
};

const FollowUps = () => {
  const { t, i18n } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [keyword, setKeyword] = useState("");
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [tab, setTab] = useState(0);
  const [expandedCards, setExpandedCards] = useState({});

  const toggleCardExpand = (id) => {
    setExpandedCards((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  useEffect(() => {
    if (!i18n.language) {
      const lang =
        localStorage.getItem("i18nextLng") || navigator.language || "en";
      i18n.changeLanguage(lang.split("-")[0]);
    }
  }, [i18n]);

  const {
    data: followUpsData,
    isLoading,
    isError,
    refetch,
  } = useFetchObjects(
    ["follow_ups", page, rowsPerPage, keyword],
    `follow_ups/?search=${keyword}&page=${page}&page_size=${rowsPerPage}`,
    token
  );

  useEffect(() => {
    refetch();
  }, [keyword, page, rowsPerPage, refetch]);

  const handleTabChange = (event, newValue) => {
    setTab(newValue);
    setPage(1);
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1);
  };

  const callCenter = followUpsData?.call_center || { results: [], count: 0 };
  const reception = followUpsData?.reception || { results: [], count: 0 };

  const callCenterColumns = [
    { key: "no", label: t("no", "No") },
    { key: "type", label: t("type", "Type") },
    { key: "full_name", label: t("name", "Name") },
    { key: "contact", label: t("contact", "Contact") },
    { key: "follow_up_date", label: t("followUpDate", "Follow Up Date") },
    { key: "procedure_date", label: t("procedureDate", "Procedure Date") },
    { key: "status", label: t("status", "Status") },
    { key: "follow_up_info", label: t("followUpInfo", "Follow Up Info") },
    { key: "action", label: t("action", "Action") },
  ];

  const receptionColumns = [
    { key: "no", label: t("no", "No") },
    { key: "type_name", label: t("type", "Type") },
    { key: "patient_full_name", label: t("name", "Name") },
    { key: "contact", label: t("contact", "Contact") },
    { key: "follow_up_date", label: t("followUpDate", "Follow Up Date") },
    { key: "procedure_date", label: t("procedureDate", "Procedure Date") },
    { key: "status_display", label: t("status", "Status") },
    { key: "follow_up_info", label: t("followUpInfo", "Follow Up Info") },
    { key: "action", label: t("action", "Action") },
  ];

  const renderFollowUpInfo = (info) => {
    if (!info) return "-";
    return (
      <Tooltip
        title={
          <div>
            {info.by && (
              <div>
                <strong>{t("followedBy", "Followed By")}:</strong>{" "}
                {info.by.name}
              </div>
            )}
            <div>
              <strong>{t("date", "Date")}:</strong> {info.date}
            </div>
            <div>
              <strong>{t("notes", "Notes")}:</strong> {info.notes}
            </div>
            {info.next_visit && (
              <div>
                <strong>{t("nextVisit", "Next Visit")}:</strong>{" "}
                {info.next_visit}
              </div>
            )}
            {info.doctors && info.doctors.length > 0 && (
              <div>
                <strong>{t("doctors", "Doctors")}:</strong>
                {info.doctors.map((doc) => doc.name).join(", ")}
              </div>
            )}
          </div>
        }
      >
        <Chip
          label={info.by ? info.by.name : t("noFollowUpBy", "No Follow Up By")}
          color="info"
          size="small"
        />
      </Tooltip>
    );
  };

  const renderCallCenterTable = () => (
    <TableContainer sx={{ mt: 3 }}>
      <Table stickyHeader size="small">
        <TableHead sx={{ bgcolor: theme.palette.grey[100] }}>
          <TableRow>
            {callCenterColumns.map((col) => (
              <TableCell key={col.key} sx={tableCellStyle}>
                {col.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {callCenter.results.map((record, i) => (
            <TableRow key={record.id} hover>
              <TableCell sx={tableCellStyle}>{i + 1}</TableCell>
              <TableCell sx={tableCellStyle}>
                <Chip
                  label={t("client", "Client")}
                  color="primary"
                  size="small"
                />
              </TableCell>
              <TableCell sx={{ ...tableCellStyle, fontWeight: "bold" }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <PersonIcon color="action" fontSize="small" />
                  <span>{record.full_name}</span>
                </Stack>
              </TableCell>
              <TableCell sx={tableCellStyle}>
                {record.phone || record.contact_2 || "-"}
              </TableCell>
              <TableCell sx={tableCellStyle}>
                {formatDateTime(record.follow_up_date) || "-"}
              </TableCell>
              <TableCell sx={tableCellStyle}>
                {formatDateTime(record.procedure_date_formatted?.date) || "-"}
              </TableCell>
              <TableCell sx={tableCellStyle}>
                <Chip
                  label={record.status_display?.display || record.status}
                  color={getStatusColor(
                    record.status_display?.value,
                    record.status_display?.display
                  )}
                  size="small"
                />
              </TableCell>
              <TableCell sx={tableCellStyle}>
                {renderFollowUpInfo(record.follow_up_info)}
              </TableCell>
              <TableCell sx={tableCellStyle}>
                <Button variant="contained" color="primary" size="small">
                  {t("followUp", "Follow Up")}
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const renderReceptionTable = () => (
    <TableContainer sx={{ mt: 3 }}>
      <Table stickyHeader size="small">
        <TableHead sx={{ bgcolor: theme.palette.grey[100] }}>
          <TableRow>
            {receptionColumns.map((col) => (
              <TableCell key={col.key} sx={tableCellStyle}>
                {col.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {reception.results.map((record, i) => (
            <TableRow key={record.id} hover>
              <TableCell sx={tableCellStyle}>{i + 1}</TableCell>
              <TableCell sx={tableCellStyle}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <MedicalServicesIcon color="action" fontSize="small" />
                  <span>{record.type_name}</span>
                </Stack>
              </TableCell>
              <TableCell sx={{ ...tableCellStyle, fontWeight: "bold" }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <PersonIcon color="action" fontSize="small" />
                  <span>{record.patient_full_name}</span>
                </Stack>
              </TableCell>
              <TableCell sx={tableCellStyle}>
                {record.patient_phone || "-"}
              </TableCell>
              <TableCell sx={tableCellStyle}>
                {formatDateTime(record.follow_up_date) || "-"}
              </TableCell>
              <TableCell sx={tableCellStyle}>
                {formatDateTime(record.procedure_date_formatted?.date) || "-"}
              </TableCell>
              <TableCell sx={tableCellStyle}>
                <Chip
                  label={
                    record.status_display?.display || record.status_display
                  }
                  color={getStatusColor(
                    record.status_display?.value,
                    record.status_display?.display
                  )}
                  size="small"
                />
              </TableCell>
              <TableCell sx={tableCellStyle}>
                {renderFollowUpInfo(record.follow_up_info)}
              </TableCell>
              <TableCell sx={tableCellStyle}>
                <Button variant="contained" color="success" size="small">
                  {t("followUp", "Follow Up")}
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const renderFollowUpDetails = (info) => {
    if (!info) {
      return (
        <Typography variant="body2" color="text.secondary">
          {t("noFollowUpInfo", "No follow-up information available")}
        </Typography>
      );
    }
    return (
      <List dense disablePadding>
        {info.by && (
          <ListItem disableGutters>
            <ListItemIcon sx={{ minWidth: 32 }}>
              <PersonPinIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary={t("followedBy", "Followed By")}
              secondary={info.by.name}
            />
          </ListItem>
        )}

        <ListItem disableGutters>
          <ListItemIcon sx={{ minWidth: 32 }}>
            <CalendarTodayIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary={t("date", "Date")}
            secondary={info.date || "-"}
          />
        </ListItem>

        <ListItem disableGutters>
          <ListItemIcon sx={{ minWidth: 32 }}>
            <NotesIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary={t("notes", "Notes")} secondary={info.notes} />
        </ListItem>

        {info.next_visit && (
          <ListItem disableGutters>
            <ListItemIcon sx={{ minWidth: 32 }}>
              <EventIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary={t("nextVisit", "Next Visit")}
              secondary={info.next_visit}
            />
          </ListItem>
        )}

        {info.doctors && info.doctors.length > 0 && (
          <ListItem disableGutters>
            <ListItemIcon sx={{ minWidth: 32 }}>
              <GroupsIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary={t("doctors", "Doctors")}
              secondary={info.doctors.map((d) => d.name).join(", ")}
            />
          </ListItem>
        )}
      </List>
    );
  };

  const renderCallCenterCards = () => (
    <Grid container spacing={2}>
      {callCenter.results.map((record, i) => (
        <Grid item xs={12} key={record.id}>
          <Card sx={{ mb: 2, borderRadius: 3, boxShadow: 3 }}>
            <CardHeader
              avatar={
                <Avatar sx={{ bgcolor: theme.palette.primary.main }}>
                  <PersonIcon />
                </Avatar>
              }
              title={
                <Typography variant="h6" fontWeight="bold">
                  {record.full_name}
                </Typography>
              }
              subheader={
                <Stack direction="row" spacing={1} alignItems="center">
                  <Chip
                    label={
                      record.procedure_name || t("noProcedure", "No Procedure")
                    }
                    size="small"
                    color="secondary"
                  />
                  <Chip
                    label={record.status_display?.display || record.status}
                    color={getStatusColor(
                      record.status_display?.value,
                      record.status_display?.display
                    )}
                    size="small"
                  />
                </Stack>
              }
              action={
                <IconButton onClick={() => toggleCardExpand(record.id)}>
                  {expandedCards[record.id] ? (
                    <ExpandLessIcon />
                  ) : (
                    <ExpandMoreIcon />
                  )}
                </IconButton>
              }
            />
            <Collapse in={expandedCards[record.id]}>
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Stack spacing={1}>
                      <Typography variant="body2">
                        <strong>{t("contact", "Contact")}:</strong>{" "}
                        {record.phone || record.contact_2 || "-"}
                      </Typography>
                      <Typography variant="body2">
                        <strong>{t("address", "Address")}:</strong>{" "}
                        {record.address || "-"}
                      </Typography>
                      <Typography variant="body2">
                        <strong>{t("ageGender", "Age/Gender")}:</strong>{" "}
                        {record.age || "-"} / {record.gender_display || "-"}
                      </Typography>
                    </Stack>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Stack spacing={1}>
                      <Typography variant="body2">
                        <strong>{t("followUpDate", "Follow Up Date")}:</strong>{" "}
                        {record.follow_up_date || "-"}
                      </Typography>
                      <Typography variant="body2">
                        <strong>{t("procedureDate", "Procedure Date")}:</strong>{" "}
                        {record.procedure_date_formatted?.full || "-"}
                      </Typography>
                    </Stack>
                  </Grid>
                  <Grid item xs={12}>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="subtitle2" gutterBottom>
                      {t("followUpDetails", "Follow Up Details")}
                    </Typography>
                    <Box
                      p={1}
                      sx={{
                        backgroundColor: theme.palette.grey[100],
                        borderRadius: 1,
                      }}
                    >
                      {renderFollowUpDetails(record.follow_up_info)}
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Collapse>
            <Box p={2}>
              <Button
                fullWidth
                variant="contained"
                color="primary"
                size="small"
              >
                {t("followUp", "Follow Up")}
              </Button>
            </Box>
          </Card>
        </Grid>
      ))}
    </Grid>
  );

  const renderReceptionCards = () => (
    <Grid container spacing={2}>
      {reception.results.map((record, i) => (
        <Grid item xs={12} key={record.id}>
          <Card sx={{ mb: 2, borderRadius: 3, boxShadow: 3 }}>
            <CardHeader
              avatar={
                <Avatar sx={{ bgcolor: theme.palette.success.main }}>
                  <MedicalServicesIcon />
                </Avatar>
              }
              title={
                <Typography variant="h6" fontWeight="bold">
                  {record.patient_full_name}
                </Typography>
              }
              subheader={
                <Stack direction="row" spacing={1} alignItems="center">
                  <Chip label={record.type_name} size="small" color="info" />
                  <Chip
                    label={
                      record.status_display?.display || record.status_display
                    }
                    color={getStatusColor(
                      record.status_display?.value,
                      record.status_display?.display
                    )}
                    size="small"
                  />
                </Stack>
              }
              action={
                <IconButton onClick={() => toggleCardExpand(record.id)}>
                  {expandedCards[record.id] ? (
                    <ExpandLessIcon />
                  ) : (
                    <ExpandMoreIcon />
                  )}
                </IconButton>
              }
            />
            <Collapse in={expandedCards[record.id]}>
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Stack spacing={1}>
                      <Typography variant="body2">
                        <strong>{t("contact", "Contact")}:</strong>{" "}
                        {record.patient_phone || "-"}
                      </Typography>
                      <Typography variant="body2">
                        <strong>{t("address", "Address")}:</strong>{" "}
                        {record.patient_address || "-"}
                      </Typography>
                      <Typography variant="body2">
                        <strong>{t("ageGender", "Age/Gender")}:</strong>{" "}
                        {record.patient_age || "-"} /{" "}
                        {record.patient_gender || "-"}
                      </Typography>
                    </Stack>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Stack spacing={1}>
                      <Typography variant="body2">
                        <strong>{t("followUpDate", "Follow Up Date")}:</strong>{" "}
                        {record.follow_up_date || "-"}
                      </Typography>
                      <Typography variant="body2">
                        <strong>{t("procedureDate", "Procedure Date")}:</strong>{" "}
                        {record.procedure_date_formatted?.full || "-"}
                      </Typography>
                    </Stack>
                  </Grid>
                  <Grid item xs={12}>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="subtitle2" gutterBottom>
                      {t("followUpDetails", "Follow Up Details")}
                    </Typography>
                    <Box
                      p={1}
                      sx={{
                        backgroundColor: theme.palette.grey[100],
                        borderRadius: 1,
                      }}
                    >
                      {renderFollowUpDetails(record.follow_up_info)}
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Collapse>
            <Box p={2}>
              <Button
                fullWidth
                variant="contained"
                color="success"
                size="small"
              >
                {t("followUp", "Follow Up")}
              </Button>
            </Box>
          </Card>
        </Grid>
      ))}
    </Grid>
  );

  return (
    <Grid container justifyContent="center" mt={1}>
      <Paper sx={{ p: 2, width: "100%", borderRadius: 2, boxShadow: 3 }}>
        <Typography
          variant="h5"
          fontWeight="bold"
          align="center"
          sx={{
            pb: 2,
            borderBottom: `5px solid ${theme.palette.primary.main}`,
            color: theme.palette.primary.main,
          }}
        >
          {t("followUps", "All Follow Ups")}
        </Typography>

        <Tabs
          value={tab}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          variant="fullWidth"
          sx={{
            mb: 2,
            borderRadius: 2,
            background: theme.palette.grey[100],
            "& .MuiTabs-indicator": {
              height: 4,
            },
          }}
        >
          <Tab
            label={
              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                fontWeight={"bold"}
              >
                <PersonIcon fontSize="small" />
                <span>{t("callCenter", "Call Center")}</span>
                <Chip
                  label={callCenter.count}
                  size="small"
                  color="primary"
                  sx={{ minWidth: 24, height: 24 }}
                />
              </Stack>
            }
          />
          <Tab
            label={
              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                fontWeight={"bold"}
              >
                <MedicalServicesIcon fontSize="small" />
                <span>{t("reception-patients", "Reception")}</span>
                <Chip
                  label={reception.count}
                  size="small"
                  color="success"
                  sx={{ minWidth: 24, height: 24 }}
                />
              </Stack>
            }
          />
        </Tabs>

        <Paper
          sx={{
            p: 2,
            mt: 1,
            mb: 2,
            borderRadius: 2,
            backgroundColor: theme.palette.grey[50],
            boxShadow: 1,
          }}
          variant="outlined"
        >
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} sm={6} md={8}>
              <TextField
                fullWidth
                variant="outlined"
                size="small"
                placeholder={t(
                  "searchPlaceholder",
                  "Search by name, phone or address..."
                )}
                value={keyword}
                onChange={(e) => {
                  setKeyword(e.target.value);
                  setPage(1);
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                  sx: {
                    borderRadius: 2,
                    backgroundColor: "white",
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Stack direction="row" spacing={2} alignItems="center">
                <FormControl size="small" sx={{ minWidth: 80 }}>
                  <Select
                    value={rowsPerPage}
                    onChange={handleRowsPerPageChange}
                    sx={{
                      borderRadius: 2,
                      backgroundColor: "white",
                    }}
                  >
                    <MenuItem value={10}>
                      10 {t("perPage", "per page")}
                    </MenuItem>
                    <MenuItem value={25}>
                      25 {t("perPage", "per page")}
                    </MenuItem>
                    <MenuItem value={50}>
                      50 {t("perPage", "per page")}
                    </MenuItem>
                    <MenuItem value={100}>
                      100 {t("perPage", "per page")}
                    </MenuItem>
                  </Select>
                </FormControl>
                <Pagination
                  count={
                    Math.ceil(
                      (tab === 0 ? callCenter.count : reception.count) /
                        rowsPerPage
                    ) || 1
                  }
                  page={page}
                  onChange={handlePageChange}
                  variant="outlined"
                  color="primary"
                  size="small"
                  siblingCount={0}
                  boundaryCount={1}
                />
              </Stack>
            </Grid>
          </Grid>
        </Paper>

        {isLoading ? (
          <Box display="flex" justifyContent="center" py={5}>
            <CircularProgress />
          </Box>
        ) : isError ? (
          <ErrorPage />
        ) : tab === 0 ? (
          callCenter.results.length === 0 ? (
            <Box textAlign="center" py={4}>
              <Typography variant="h6" color="text.secondary">
                {t("noClientsFound", "No clients found")}
              </Typography>
              <Typography variant="body2" color="text.secondary" mt={1}>
                {t("tryDifferentSearch", "Try a different search term")}
              </Typography>
            </Box>
          ) : isMobile ? (
            renderCallCenterCards()
          ) : (
            renderCallCenterTable()
          )
        ) : reception.results.length === 0 ? (
          <Box textAlign="center" py={4}>
            <Typography variant="h6" color="text.secondary">
              {t("noPatientsFound", "No patients found")}
            </Typography>
            <Typography variant="body2" color="text.secondary" mt={1}>
              {t("tryDifferentSearch", "Try a different search term")}
            </Typography>
          </Box>
        ) : isMobile ? (
          renderReceptionCards()
        ) : (
          renderReceptionTable()
        )}

        <Box mt={3} display="flex" justifyContent="center">
          <Pagination
            count={
              Math.ceil(
                (tab === 0 ? callCenter.count : reception.count) / rowsPerPage
              ) || 1
            }
            page={page}
            onChange={handlePageChange}
            variant="outlined"
            color="primary"
            size="large"
            showFirstButton
            showLastButton
          />
        </Box>
      </Paper>
    </Grid>
  );
};

export default FollowUps;
